import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, Download, LogOut } from "lucide-react";
import Lightbox from "../components/Lightbox";
import { useMeta } from "../lib/useMeta";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const EASE = [0.22, 1, 0.36, 1];
const tokenKey = (slug) => `gallery_token_${slug}`;

export default function ClientGallery() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [token, setToken] = useState(() => sessionStorage.getItem(tokenKey(slug)));
  const [gallery, setGallery] = useState(null);
  const [passcode, setPasscode] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  useMeta(meta?.title ? `${meta.title} · Private Gallery` : "Private Gallery");

  useEffect(() => {
    axios.get(`${BACKEND}/api/client/${slug}`).then((r) => setMeta(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  const lock = useCallback(() => {
    sessionStorage.removeItem(tokenKey(slug));
    setToken(null);
    setGallery(null);
  }, [slug]);

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${BACKEND}/api/client/${slug}/photos`, { params: { t: token } })
      .then((r) => setGallery(r.data))
      .catch(() => lock());
  }, [token, slug, lock]);

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await axios.post(`${BACKEND}/api/client/${slug}/unlock`, { passcode });
      sessionStorage.setItem(tokenKey(slug), data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "That passcode is not correct");
    } finally {
      setBusy(false);
    }
  };

  const frames = (gallery?.photos ?? []).map((p) => ({ ...p, url: `${BACKEND}${p.url}`, title: p.filename, category: "", location: "", story: "" }));

  if (notFound) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6" data-testid="client-gallery-not-found">
        <div className="text-center">
          <p className="font-serif text-3xl italic text-stone-400">This gallery could not be found.</p>
          <Link to="/" className="mt-8 inline-block text-[11px] font-mono tracking-[0.2em] uppercase text-champagne">Return to site</Link>
        </div>
      </main>
    );
  }

  if (!token || !gallery) {
    return (
      <main className="min-h-screen flex items-center justify-center px-6 bg-[#0D0D10]" data-testid="client-gallery-locked">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: EASE }} className="w-full max-w-md border border-white/10 bg-elevated p-10">
          <p className="inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.25em] uppercase text-stone-500"><Lock size={12} /> Private Gallery</p>
          <h1 className="mt-3 font-serif text-3xl text-ivory leading-tight" data-testid="client-gallery-title">{meta?.title || "…"}</h1>
          {meta?.couple && <p className="mt-2 font-serif italic text-champagne">{meta.couple}</p>}
          {token && !gallery ? (
            <p className="mt-8 text-[11px] font-mono tracking-[0.3em] uppercase text-stone-500 animate-pulse">Opening your gallery…</p>
          ) : (
            <form onSubmit={unlock} className="mt-8 space-y-6" data-testid="client-unlock-form">
              <div>
                <label htmlFor="gallery-passcode" className="text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500">Passcode</label>
                <input id="gallery-passcode" data-testid="client-passcode-input" type="password" required value={passcode} onChange={(e) => setPasscode(e.target.value)} className="w-full bg-transparent border-b border-white/15 focus:border-champagne outline-none py-3 text-sm font-light text-ivory placeholder:text-stone-600 transition-colors" placeholder="Enter the code we shared with you" />
              </div>
              {error && <p data-testid="client-unlock-error" className="text-xs font-light text-terracotta">{error}</p>}
              <button type="submit" data-testid="client-unlock-submit" disabled={busy} className="w-full bg-champagne text-obsidian px-10 py-4 text-[11px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors disabled:opacity-50">{busy ? "Unlocking…" : "Open Gallery"}</button>
            </form>
          )}
          <p className="mt-8 text-[10px] font-mono tracking-[0.2em] uppercase text-stone-600">Élégance Noire & Lumière</p>
        </motion.div>
      </main>
    );
  }

  const zipUrl = `${BACKEND}/api/client/${slug}/zip?t=${token}`;

  return (
    <main className="min-h-screen bg-obsidian" data-testid="client-gallery-open">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.25em] uppercase text-stone-500"><Lock size={11} /> Private Gallery · {frames.length} photographs</p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl text-ivory leading-tight">{gallery.title}</h1>
            {gallery.couple && <p className="mt-1 font-serif italic text-champagne">{gallery.couple}</p>}
          </div>
          <div className="flex items-center gap-2">
            {frames.length > 0 && (
              <a href={zipUrl} data-testid="client-download-all" onClick={() => toast.success("Preparing your ZIP — this may take a moment")} className="inline-flex items-center gap-2 bg-champagne text-obsidian px-5 py-3 text-[10px] font-mono tracking-[0.2em] uppercase hover:bg-ivory transition-colors">
                <Download size={13} /> Download All
              </a>
            )}
            <button data-testid="client-lock-button" onClick={lock} className="inline-flex items-center gap-2 border border-white/15 px-4 py-3 text-[10px] font-mono tracking-[0.2em] uppercase text-stone-300 hover:border-champagne hover:text-champagne transition-colors"><LogOut size={12} /> Lock</button>
          </div>
        </div>
      </header>

      {gallery.welcome && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
          <p data-testid="client-gallery-welcome" className="max-w-2xl font-serif text-xl italic leading-relaxed text-stone-300 whitespace-pre-line">{gallery.welcome}</p>
        </div>
      )}

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {frames.length === 0 ? (
          <p data-testid="client-gallery-empty" className="font-serif text-2xl italic text-stone-500">Your photographs are being finished — return soon.</p>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5" data-testid="client-gallery-grid">
            {frames.map((f, i) => (
              <motion.figure
                key={f.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.7, ease: EASE }}
                data-testid={`client-photo-${f.id}`}
                className="group relative break-inside-avoid overflow-hidden bg-surface"
              >
                <img src={f.url} alt={f.filename} loading="lazy" onClick={() => setLightboxIndex(i)} className="w-full cursor-zoom-in object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]" />
                <a
                  href={`${f.url}&download=1`}
                  data-testid={`client-photo-download-${f.id}`}
                  aria-label={`Download ${f.filename}`}
                  className="absolute bottom-3 right-3 p-2.5 bg-black/60 text-stone-200 opacity-0 group-hover:opacity-100 hover:text-champagne transition-all"
                >
                  <Download size={14} />
                </a>
              </motion.figure>
            ))}
          </div>
        )}
      </section>

      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox images={frames} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={setLightboxIndex} />
        )}
      </AnimatePresence>
    </main>
  );
}
